/**
 * 계약서 빈칸 채우기 — 값은 전부 HTML 이스케이프한다(업체명·주소는 사람이 친 글자다).
 * 비어 있는 칸은 밑줄 빈칸으로 남겨 손으로 쓸 수 있게 한다.
 */
import type { ContractInput } from '@geo-admin/shared/contract/api/contract.ts';
import { CONTRACT_TEMPLATE } from './template.ts';

// Vite 가 번들 폰트의 최종 주소로 바꾼다 — iframe 문서 안에서도 같은 오리진이라 CSP 를 통과한다
const FONT_URL = new URL('../assets/fonts/SUIT-Variable.woff2', import.meta.url).href;
const SEAL_URL = new URL('../assets/company-seal.png', import.meta.url).href;

export type ContractFields = Omit<ContractInput, 'brandId'>;

const esc = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const blank = (wide = true) => `<span class="blank${wide ? ' w' : ''}">　</span>`;
const text = (v: string) => (v.trim() === '' ? blank() : esc(v.trim()));

/** 2026-10-20 → 2026년 10월 20일 */
export function koreanDate(d: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) return blank(false);
  return `${m[1]}년 ${Number(m[2])}월 ${Number(m[3])}일`;
}

/**
 * 인쇄·PDF 의 기본 파일명 — `liveGPT계약서_업체명`. 브라우저는 문서 제목을 파일명으로 쓴다.
 * 파일명에 못 쓰는 글자(\ / : * ? " < > |)는 뺀다.
 */
export function contractFileTitle(companyName: string): string {
  const name = companyName
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return name ? `liveGPT계약서_${name}` : 'liveGPT계약서';
}

export function renderContract(f: ContractFields): string {
  const queries = f.queries.map((q) => q.trim()).filter(Boolean);
  const queryRows = queries.map((q, i) => `  <tr><td>${i + 1}</td><td>${esc(q)}</td></tr>`).join('\n');
  const values: Record<string, string> = {
    fontUrl: FONT_URL,
    sealUrl: SEAL_URL,
    docTitle: esc(contractFileTitle(f.companyName)),
    companyName: text(f.companyName),
    representativeName: text(f.representativeName),
    businessNumber: text(f.businessNumber),
    address: text(f.address),
    contact: text(f.contact),
    startDate: koreanDate(f.startDate),
    contractDate: koreanDate(f.contractDate),
    monthlyFee: f.monthlyFee > 0 ? f.monthlyFee.toLocaleString('ko-KR') : blank(),
    extraQueryFee: f.extraQueryFee > 0 ? f.extraQueryFee.toLocaleString('ko-KR') : blank(),
    queryRows: queryRows || `  <tr><td>1</td><td>${blank()}</td></tr>`,
  };
  return CONTRACT_TEMPLATE.replace(/\{\{(\w+)\}\}/g, (all, key: string) => values[key] ?? all);
}
